import {HttpErrorResponse} from '@angular/common/http';
import {ChangeDetectionStrategy, Component, HostBinding, OnDestroy, OnInit, inject, input} from '@angular/core';
import {AbstractControl, FormBuilder, Validators} from '@angular/forms';
import {Router} from '@angular/router';
import {Store, select} from '@ngrx/store';
import {BehaviorSubject, Observable, Subscription, combineLatest, filter, map, startWith, take} from 'rxjs';
import * as moment from 'moment-timezone';
import {ALLOWED_IMAGE_TYPES, IMAGE_UPLOAD_MAX_SIZE} from 'src/app/app.constants';
import {environment} from 'src/environments/environment';
import {getFrontendDomain} from 'src/environments/api-environments.constants';
import {MobileService} from 'src/app/core/page/mobile.service';
import {Role} from 'src/app/core/model/role';
import {SkillModel} from 'src/app/core/model/skill';
import {User} from 'src/app/core/model/user';
import {TitleService} from 'src/app/core/page/title.service';
import {DeleteImageAction, GetActiveUserAction} from 'src/app/core/store/active-user/active-user.action';
import {selectActiveUser, selectActiveUserRole} from 'src/app/core/store/active-user/active-user.selector';
import {GetLensesAction} from 'src/app/core/store/lenses/lenses.action';
import {selectLenses, selectLensesMap} from 'src/app/core/store/lenses/lenses.selector';
import {selectAllRoles, selectRolesMap} from 'src/app/core/store/roles/roles.selector';
import {selectRouterParam} from 'src/app/core/store/router/router.selector';
import {FetchSkillsAction} from 'src/app/core/store/skills/skills.action';
import {selectSkillsList, selectSkillsMap} from 'src/app/core/store/skills/skills.selector';
import {GetTeamsAction} from 'src/app/core/store/teams/teams.action';
import {selectAllTeams, selectTeamsMap} from 'src/app/core/store/teams/teams.selector';
import {
  CreateNewUserAction,
  GetAllUsersAction,
  SendUserEmailAction,
  UpdateUserAction,
} from 'src/app/core/store/users/users.action';
import {selectAllUsers} from 'src/app/core/store/users/users.selector';
import {Lens} from 'src/app/lenses/shared/model/lens';
import {FileUploadService} from 'src/app/services/file-upload.service';
import {MessageService} from 'src/app/services/message.service';
import {Team} from 'src/app/teams/shared/model/team';
import {GetAllRolesAction} from '../../core/store/roles/roles.action';
import {TimezoneOptionsService} from '../../services/timezone-options.service';
import {UserDetailOption} from '../model/user-detail-option';
import {UpdateUserProfileAction} from './../../core/store/users/users.action';
import {passwordMatchValidator} from './user-form-validators';
import {TimezoneService} from 'src/app/services/timezone.service';

@Component({
  selector: 'user-detail',
  templateUrl: './user-detail.component.html',
  styleUrls: ['./user-detail.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserDetailComponent implements OnInit, OnDestroy {
  @HostBinding('class') hostClasses = 'oph-feature-layout oph-feature-padding';

  isUserProfileView = input<boolean>(false);

  private timezoneService = inject(TimezoneService);

  activeUser$: Observable<User> = this.store$.pipe(select(selectActiveUser));
  id$: Observable<string> = this.store$.pipe(select(selectRouterParam('id')));
  users$: Observable<User[]> = this.store$.pipe(select(selectAllUsers));
  teams$: Observable<Team[]> = this.store$.pipe(select(selectAllTeams));
  teamsMap$: Observable<Record<string, Team>> = this.store$.pipe(select(selectTeamsMap));
  teamOptions$: Observable<UserDetailOption[]> = this.store$.pipe(select(selectAllTeams)).pipe(
    startWith([]),
    filter((teams: Team[]) => !!teams),
    map((teams: Team[]) => teams?.map(team => ({id: team.id, display: team.name})))
  );
  roles$: Observable<Role[]> = this.store$.pipe(select(selectAllRoles));
  rolesMap$: Observable<Record<string, Role>> = this.store$.pipe(select(selectRolesMap));
  roleOptions$: Observable<UserDetailOption[]> = this.store$.pipe(select(selectAllRoles)).pipe(
    filter((roles: Role[]) => !!roles),
    map(roles => roles.map(role => ({id: role.id, display: role.displayName})))
  );
  lenses$: Observable<Lens[]> = this.store$.pipe(select(selectLenses));
  lensesMap$: Observable<Record<string, Lens>> = this.store$.pipe(select(selectLensesMap));
  lensOptions$: Observable<UserDetailOption[]> = this.store$.pipe(select(selectLenses)).pipe(
    startWith([]),
    filter((lenses: Lens[]) => !!lenses),
    map(lenses => lenses?.map(lens => ({id: lens._id, display: lens.title})))
  );
  skills$: Observable<SkillModel[]> = this.store$.pipe(select(selectSkillsList));
  skillsMap$: Observable<Record<string, SkillModel>> = this.store$.pipe(select(selectSkillsMap));
  skillOptions$: Observable<UserDetailOption[]> = this.store$
    .pipe(select(selectSkillsList))
    .pipe(map(skills => skills.map(skill => ({id: skill._id, display: skill.name})).filter(skill => skill.display)));

  timezoneOptions$: Observable<UserDetailOption[]>;

  user$ = new BehaviorSubject<User>(null);
  loadingSave$ = new BehaviorSubject<boolean>(false);
  avatarUrl$ = new BehaviorSubject<string>('');
  avatarImgLoading$ = new BehaviorSubject<boolean>(false);
  uploadProgress$ = new BehaviorSubject<number>(0);
  sendEmailLoading$ = new BehaviorSubject<boolean>(false);
  initialTimezone$ = new BehaviorSubject<string>('');

  subscriptions = new Subscription();
  selectedFile: File = null;
  allowedImageTypes = ALLOWED_IMAGE_TYPES;
  maxFileSize = IMAGE_UPLOAD_MAX_SIZE;
  tempImageUrl: string = null;
  deletedImgs: string[] = [];
  deleteHovered = false;

  isActiveUserAdmin$: Observable<boolean>;
  activeUserRole$: Observable<Role> = this.store$.pipe(select(selectActiveUserRole));

  constructor(
    private store$: Store,
    private fb: FormBuilder,
    private router: Router,
    private messageService: MessageService,
    private titleService: TitleService,
    private fileUploadService: FileUploadService,
    private timezoneOptionsService: TimezoneOptionsService,
    private mobileService: MobileService
  ) {}

  form = this.fb.group(
    {
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      username: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      password: [''],
      confirmPassword: [''],
      roles: [[], Validators.required],
      teams: [],
      lens: null,
      skills: [],
      requirements: [],
      avatarUrl: [''],
      timezone: [''],
    },
    {validators: passwordMatchValidator}
  );

  ngOnInit() {
    this.store$.dispatch(new GetAllUsersAction({}));
    this.store$.dispatch(new GetTeamsAction({}));
    this.store$.dispatch(new GetLensesAction({}));
    this.store$.dispatch(new GetAllRolesAction({}));
    this.store$.dispatch(new FetchSkillsAction({}));
    this.subscriptions.add(this.subscribeToUserId());
    this.isActiveUserAdmin$ = this.activeUserRole$.pipe(map(role => role?.displayName?.toLowerCase() === 'admin'));

    this.timezoneOptions$ = new BehaviorSubject([
      {
        id: moment.tz.guess(),
        display: 'Auto',
      },
      ...this.timezoneOptionsService.getTimezoneOptions().map(tz => ({
        id: tz.value,
        display: tz.display,
      })),
    ]);
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  subscribeToUserId(): Subscription {
    if (this.isUserProfileView()) {
      return combineLatest([this.activeUser$, this.users$])
        .pipe(
          filter(([user, users]) => !!user && !!users),
          take(1)
        )
        .subscribe(([activeUser, users]) => {
          const foundUser = users.find(user => user._id === activeUser._id);
          this.user$.next(foundUser);
          this.initialTimezone$.next(foundUser?.timezone || '');
          this.titleService.setPageTitle(foundUser?.username || 'Name not found', 'Edit User');
          foundUser && this.fillForm(foundUser);
        });
    }
    return combineLatest([this.id$, this.users$])
      .pipe(filter(id => !!id))
      .subscribe(([id, users]) => {
        if (id !== 'new') {
          if (!users) {
            return;
          }
          const user = (users || []).find(user => user._id === id);
          this.user$.next(user);
          this.titleService.setPageTitle(user?.username || 'Name not found', 'Edit User');
          user && this.fillForm(user);
        } else {
          this.titleService.setPageTitle('New User');
          this.passwordControl.setValidators([Validators.required]);
          this.passwordControl.updateValueAndValidity();
        }
      });
  }

  fillForm(user: User) {
    this.form.setValue({
      firstName: user.firstName,
      lastName: user.lastName,
      username: user.username,
      email: user.email || '',
      password: '',
      confirmPassword: '',
      roles: user.roles as null,
      teams: user?.teams || [],
      lens: user.lens || '',
      requirements: user.requirements as null,
      skills: user.skills as null,
      avatarUrl: user.avatarUrl || '',
      timezone: user.timezone || '',
    });
    this.avatarUrl$.next(user.avatarUrl || '');
  }

  onBack() {
    // If the user clicks back and has not saved, delete the avatar img from s3 if one was added
    if (!this.user$?.value?.avatarUrl && this.avatarUrl$?.value) {
      const ids = encodeURIComponent(this.avatarUrl$.value);
      this.deletedImgs.push(ids);
    }
    this.checkDeletedImgs();
    this.router.navigate(['people/users']);
  }

  onRoleChange({display, id}: UserDetailOption) {
    const role = {id, displayName: display};
    this.rolesControl.setValue(role ? [role] : []);
  }

  onTeamChange({display, id}: UserDetailOption) {
    const team = {id, name: display};
    this.teamsControl.setValue(team ? [team] : []);
  }

  onLensChange({display, id}: UserDetailOption) {
    const lens = {id, title: display};
    this.lensControl.setValue(lens);
  }

  onTimezoneChange({display, id}: UserDetailOption) {
    this.timezoneControl.setValue(id);
  }

  getTimezoneDisplayText(): string {
    const currentTimezone = this.timezoneControl.value;
    if (!currentTimezone) {
      return 'Select Timezone';
    }

    const timezoneOption = this.timezoneOptionsService.getCurrentTimezoneOption(currentTimezone);
    return timezoneOption ? timezoneOption.display : currentTimezone;
  }

  onAddSkill({display, id}: UserDetailOption) {
    const currentSkills = this.skillsControl.value || [];
    if (!currentSkills.includes(id)) {
      const updatedSkills = [...currentSkills, id];
      this.skillsControl.setValue(updatedSkills);
    }
  }

  onRemoveSkill(id: string) {
    const filteredSkills = this.skillsControl.value.filter(skillId => skillId !== id);
    this.skillsControl.setValue(filteredSkills);
  }

  onSave(id: string) {
    this.form.markAllAsTouched();
    if (this.form.invalid) {
      this.messageService.add('Please fill out all required fields', 'error');
      return;
    }

    this.loadingSave$.next(true);

    // if the user is updating their own profile, use updateUserProfile action
    if (this.isUserProfileView() && !id) {
      this.store$.dispatch(
        new UpdateUserProfileAction({
          user: this.getUserDto(this.user$.value?._id),
          onSuccess: (user: User) => this.onUserSuccess(user),
          onFailure: err => this.onUserFailure(err),
        })
      );
    } else if (id === 'new') {
      this.store$.dispatch(
        new CreateNewUserAction({
          user: this.getUserDto(id),
          onSuccess: (user: User) => this.onUserSuccess(user),
          onFailure: err => this.onUserFailure(err),
        })
      );
    } else {
      this.store$.dispatch(
        new UpdateUserAction({
          user: this.getUserDto(id),
          onSuccess: (user: User) => this.onUserSuccess(user),
          onFailure: err => this.onUserFailure(err),
        })
      );
    }
  }

  onUserSuccess(user: User) {
    const userId = user._id || this.user$.value?._id;
    this.user$.next(user);
    this.store$.dispatch(
      new GetActiveUserAction({
        onSuccess: (activeUser: User) => {
          if (this.isUserProfileView() || activeUser.email === this.user$.value.email) {
            this.messageService.add('Profile updated successfully', 'taskCompleted');
            this.router.navigate(['/profile']);
            this.loadingSave$.next(false);

            if (this.initialTimezone$.value !== activeUser.timezone) {
              window.location.reload();
            }
            return;
          }

          this.messageService.add('User updated successfully', 'taskCompleted');
          this.checkDeletedImgs();
          this.loadingSave$.next(false);
          this.router.navigate(['people/users', userId]);
          this.store$.dispatch(new GetAllUsersAction({}));
        },
      })
    );
  }

  // eslint-disable-next-line
  onUserFailure(err: any) {
    const userNameError = err.errors?.username?.kind === 'unique';
    const pinError = err.errors?.pin?.kind === 'unique';
    const emailError = err.errors?.email?.kind === 'unique';

    let errorMessage;
    if (userNameError && pinError) {
      errorMessage = 'This username and pin are already taken. Please choose different ones.';
    } else if (userNameError) {
      errorMessage = 'This username is already taken. Please choose a different one.';
    } else if (pinError) {
      errorMessage = 'This pin is already taken. Please choose a different one.';
    } else if (emailError) {
      errorMessage = 'This email is already taken. Please choose a different one.';
    }
    this.messageService.add(errorMessage || 'An error occurred. Please try again.');
    this.loadingSave$.next(false);
  }

  getUserDto(id: string): User {
    // needed for the API to create or update
    const extractTeamIds = (teams: Team[]): string[] =>
      teams?.length ? teams.map(team => team.id || team._id).filter(id => !!id) : [];
    const extractRoleIds = (roles: Role[]): string[] =>
      roles?.length ? roles.map(role => role.id || role._id).filter(id => !!id) : [];
    const extractLensId = (lens: UserDetailOption | Lens): string => {
      if (!lens) return '';
      if (typeof lens === 'string') return lens;
      if (lens['id']) return lens['id'];
      if (lens['_id']) return lens['_id'];
      return '';
    };

    const user = {
      _id: id,
      firstName: this.firstNameControl.value.trim(),
      lastName: this.lastNameControl.value.trim(),
      lens: extractLensId(this.lensControl.value) as unknown as Lens,
      requirements: this.skillsControl.value || [],
      roles: extractRoleIds(this.rolesControl.value) as unknown as Role[],
      teams: extractTeamIds(this.teamsControl.value) as unknown as Team[],
      username: this.usernameControl.value.trim(),
      password: this.passwordControl.value,
      email: this.emailControl.value.trim().toLowerCase(),
      avatarUrl: this.avatarUrlControl.value,
      timezone: this.timezoneControl.value,
    };

    if (id === 'new') {
      delete user._id;
    }
    return user;
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];

      const validationError = this.fileUploadService.validateFile(file);
      if (validationError) {
        this.messageService.add(validationError);
        return;
      }

      this.avatarImgLoading$.next(true);
      this.selectedFile = file;
      this.tempImageUrl = URL.createObjectURL(file);

      // Get user ID for prefix
      this.activeUser$.pipe(take(1)).subscribe(user => {
        this.fileUploadService.uploadFile(file, `${user._id}/`).subscribe({
          next: result => {
            this.uploadProgress$.next(result.progress);
            if (result.url) {
              this.onUploadImageSuccess(result.url);
            }
          },
          error: (error: HttpErrorResponse) => {
            this.onUploadImageFailure(error);
          },
        });
      });
    }
  }

  onUploadImageSuccess(url: string) {
    this.avatarUrlControl.setValue(url);
    this.avatarUrl$.next(url);
    this.avatarImgLoading$.next(false);
    this.uploadProgress$.next(0);
  }

  onUploadImageFailure(err: HttpErrorResponse) {
    this.messageService.add(err.message || 'There was a problem uploading the image.');
    this.avatarImgLoading$.next(false);
    this.uploadProgress$.next(0);
  }

  onDeleteAvatarImg() {
    const ids = encodeURIComponent(this.avatarUrl$.value);
    this.deletedImgs.push(ids);
    this.avatarUrlControl.setValue('');
    this.avatarUrl$.next('');
  }

  checkDeletedImgs() {
    if (this.deletedImgs.length) {
      const ids = this.deletedImgs.join(',');
      this.store$.dispatch(
        new DeleteImageAction({
          ids,
        })
      );
    }
  }

  onSendUserEmail() {
    this.sendEmailLoading$.next(true);
    const timestamp = this.timezoneService.convertDateToUtcString(
      moment().tz(this.timezoneService.getTimeZone()).toDate()
    );
    const domain = getFrontendDomain(environment.envName, this.mobileService.isMobilePlatform());
    const url = `${domain}/user-request`;
    this.activeUser$.pipe(take(1)).subscribe(activeUser => {
      const activeUserName =
        activeUser.firstName.toLowerCase() === 'admin' || activeUser.lastName.toLowerCase() === 'admin'
          ? 'An admin'
          : `${activeUser.firstName} ${activeUser.lastName}`;
      this.store$.dispatch(
        new SendUserEmailAction({
          userId: this.user$.value._id,
          timestamp,
          activeUserId: activeUser._id,
          activeUserName,
          url,
          onSuccess: () => {
            this.messageService.add('Email sent successfully', 'taskCompleted');
            this.sendEmailLoading$.next(false);
          },
          onFailure: error => {
            this.messageService.add(error, 'error');
            this.sendEmailLoading$.next(false);
          },
        })
      );
    });
  }

  get firstNameControl(): AbstractControl {
    return this.form.get('firstName');
  }

  get lastNameControl(): AbstractControl {
    return this.form.get('lastName');
  }

  get usernameControl(): AbstractControl {
    return this.form.get('username');
  }

  get passwordControl(): AbstractControl {
    return this.form.get('password');
  }

  get confirmPasswordControl(): AbstractControl {
    return this.form.get('confirmPassword');
  }

  get rolesControl(): AbstractControl {
    return this.form.get('roles');
  }

  get teamsControl(): AbstractControl {
    return this.form.get('teams');
  }

  get lensControl(): AbstractControl {
    return this.form.get('lens');
  }

  get skillsControl(): AbstractControl {
    // requirements form option is used becaused skills are not used
    return this.form.get('requirements');
  }

  get emailControl(): AbstractControl {
    return this.form.get('email');
  }

  get avatarUrlControl(): AbstractControl {
    return this.form.get('avatarUrl');
  }

  get timezoneControl(): AbstractControl {
    return this.form.get('timezone');
  }
}
